import { changePackageHeading } from '@/features/dashboard/bookingChanges/types/BookingChangeKind.utils.ts';
import { cn } from '@/lib/utils';
import {
  ChangeDraftVm,
  ChangePackageHistoryVm,
} from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import {
  getChangeImpactLabel,
  getChangePackageStatusLabel,
} from '@/features/dashboard/bookingChanges/types/ChangeViewModel.utils.ts';
import { formatDayAndMonth } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import RecentChangesDraftRow from './RecentChangesDraftRow';
import RecentChangesEntry from './RecentChangesEntry';
import {
  InfoCard,
  InfoCardBody,
  InfoCardHeader,
} from '@/components/ui/InfoCard.tsx';

interface RecentChangesCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Otwarty szkic — wyróżniony wiersz na górze listy. */
  draft?: ChangeDraftVm;
  /** Zamknięte paczki, od najnowszej. */
  history: ChangePackageHistoryVm[];
}

/** Etykieta statusu stoi w zdaniu, więc schodzi z wielkiej litery. */
const packageTitle = (entry: ChangePackageHistoryVm): string =>
  `${changePackageHeading(entry.kind, entry.packageNumber)} · ${getChangePackageStatusLabel(entry.status).toLowerCase()}`;

/**
 * Data, autor i kwota po kropkach. Autora bywa brak (paczka bez rozpoznanego
 * pracownika w audycie), więc segment wypada zamiast zostawiać pustkę między
 * separatorami.
 */
const packageMeta = (entry: ChangePackageHistoryVm): string =>
  [
    formatDayAndMonth(entry.occurredAt),
    entry.author,
    getChangeImpactLabel(entry.impact),
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Karta „Ostatnie zmiany” — panel boczny w trybie podglądu. Otwarty szkic
 * stoi nad historią i mówi wprost, że czeka na wprowadzenie; niżej paczki,
 * które już się zamknęły.
 */
const RecentChangesCard = ({
  draft,
  history,
  ...props
}: RecentChangesCardProps) => (
  <InfoCard {...props}>
    <InfoCardHeader title="Ostatnie zmiany" />

    {draft && <RecentChangesDraftRow draft={draft} />}

    <InfoCardBody className="gap-3">
      {history.map((entry, index) => (
        <RecentChangesEntry
          key={entry.id}
          title={packageTitle(entry)}
          meta={packageMeta(entry)}
          className={cn(index > 0 && 'border-t border-neutral-100 pt-3')}
        />
      ))}
    </InfoCardBody>
  </InfoCard>
);

export default RecentChangesCard;
